/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { utils } from 'pixi.js-legacy';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Swimlane, SwimlaneRenderTypes } from 'src/app/app-data/models/Swimlane';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { SwimlaneArea } from './SwimlaneArea';
import { SwimlaneLabel } from './SwimlaneLabel';
import { SwimlaneLine } from './SwimlaneLine';
import { SWIMLANE_GRAPHICS, SWIMLANE_SPOT } from './SwimlaneConst';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';
import { ISwimlaneScreenPosition } from '@appPlanApplication/swimlanes/FancySwimlanes';
import { glob } from 'typedoc/dist/lib/utils/fs';

export class SwimlaneContainer extends FancyContainer {
	public override visible = true;
	public label: SwimlaneLabel;
	public area: SwimlaneArea;
	public topLine: SwimlaneLine;
	public bottomLine: SwimlaneLine;
	public lineContainer: FancyContainer;
	public dragCircleTop: FancySprite;
	public dragCircleBottom: FancySprite;
	private data: Swimlane;

	constructor(swimlaneModel: Swimlane, private canvasAngularLand: CanvasAngularLand) {
		super();

		this.label = new SwimlaneLabel(swimlaneModel, this.canvasAngularLand);
		this.area = new SwimlaneArea(swimlaneModel, this.canvasAngularLand);
		this.topLine = new SwimlaneLine(swimlaneModel, this.canvasAngularLand, SWIMLANE_SPOT.TOP);
		this.bottomLine = new SwimlaneLine(swimlaneModel, this.canvasAngularLand, SWIMLANE_SPOT.BOTTOM);
		this.dragCircleTop = new FancySprite(utils.TextureCache[SWIMLANE_GRAPHICS.TEXTURE_SWIMLANE_DRAG_HANDLE]);
		this.dragCircleBottom = new FancySprite(
			utils.TextureCache[SWIMLANE_GRAPHICS.TEXTURE_SWIMLANE_DRAG_HANDLE]
		);

		this.dragCircleTop.width = 30;
		this.dragCircleTop.height = 15;
		this.dragCircleBottom.width = 30;
		this.dragCircleBottom.height = 15;

		this.lineContainer = new FancyContainer();

		this.lineContainer.interactiveChildren = true;

		this.lineContainer.addChild(
			this.topLine,
			this.bottomLine,
			this.dragCircleTop as any,
			this.dragCircleBottom
		);

		// The X is always 84 (width of the sidebar)
		// MOC-3022. Print has a gap on left side when doing this. So change to 0.
		this.x = 0;

		// Add our children to the container
		this.addChild(this.area);
	}
	/**
	 * Although the SwimlaneLabel lives in the SwimlaneContainer, we add it to the UI layer separately instead of as a child
	 * of the SwimlaneContainer. This is because scaling of the SwimlaneArea will also distort the children, and create blurred text
	 *
	 * Returns the SwimlaneLabel assigned to this swimlane
	 */
	public getLabel(): SwimlaneLabel {
		return this.label;
	}

	public getArea(): SwimlaneArea {
		return this.area;
	}

	public update(globalWidth: number, swimlaneModel: Swimlane): void {
		this.data = swimlaneModel;

		// Get current mode
		const mode: string =
			swimlaneModel.currentRenderType === SwimlaneRenderTypes.edit ? SwimlaneRenderTypes.edit : 'view';
		const screenPosition: ISwimlaneScreenPosition = {
			screenBottom: swimlaneModel.view.screenBottom as number,
			screenTop: swimlaneModel.view.screenTop as number,
		};

		this.updatePosition(globalWidth, screenPosition);

		this.topLine.setColor(swimlaneModel[mode].color);
		this.bottomLine.setColor(swimlaneModel[mode].color);

		// Set swimlane color if changed
		this.area.setColor(swimlaneModel[mode].color);

		// Make interactive
		if (mode === SwimlaneRenderTypes.edit) {
			this.area.eventMode = EventMode.Static;
			this.area.cursor = CursorType.Pointer;

			this.label.visible = false;
			this.label.background.visible = false;

			this.topLine.setEdit(globalWidth, true);
			this.bottomLine.setEdit(globalWidth, true);
			// Position our lines
			this.dragCircleTop.visible = true;
			this.dragCircleTop.x = globalWidth / 2 - this.dragCircleTop.width / 2;
			this.dragCircleTop.y = this.topLine.y - this.dragCircleTop.height / 2;
			this.dragCircleBottom.visible = true;
			this.dragCircleBottom.x = globalWidth / 2 - this.dragCircleBottom.width / 2;
			this.dragCircleBottom.y = this.bottomLine.y - this.dragCircleBottom.height / 2;
		} else if (swimlaneModel.currentRenderType === SwimlaneRenderTypes.database) {
			this.area.eventMode = EventMode.None;
			this.area.cursor = CursorType.None;

			this.label.visible = true;
			this.label.background.visible = true;

			this.topLine.setEventMode(EventMode.Disabled);
			this.bottomLine.setEventMode(EventMode.Disabled);

			this.dragCircleTop.visible = false;
			this.dragCircleBottom.visible = false;
		}

		// Update the label
		this.label.update(swimlaneModel);
	}

	public updatePosition(globalWidth: number, screenPosition: ISwimlaneScreenPosition): void {
		// Set swimlane Y
		this.y = screenPosition.screenTop;

		// Set SwimlaneLine dimensions
		this.topLine.y = screenPosition.screenTop;
		this.bottomLine.y = screenPosition.screenBottom;
		this.topLine.width = globalWidth;
		this.topLine.height = 1;
		this.bottomLine.width = globalWidth;
		this.bottomLine.height = 1;

		// Set swimlane area dimensions
		this.area.width = globalWidth;
		this.area.height = screenPosition.screenBottom - screenPosition.screenTop;
		this.label.updatePosition(screenPosition);
	}

	public override destroy(): void {
		super.destroy();
		this.label.destroy();
		this.area.destroy();
		this.topLine.destroy();
		this.bottomLine.destroy();
		this.dragCircleTop.destroy();
		this.dragCircleBottom.destroy();
		this.topLine = null;
		this.bottomLine = null;
		this.dragCircleBottom = null;
		this.dragCircleTop = null;

		this.label = null;
		this.area = null;
		this.data = null;
		this.canvasAngularLand = null;
	}

	public hideLabel(): void {
		this.label.visible = false;
		this.label.background.visible = false;
	}

	public showLabel(): void {
		this.label.visible = true;
		this.label.background.visible = true;
	}

	public override getData(): Swimlane {
		return this.data;
	}
}
